import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, CheckCircle2 } from "lucide-react";
import { api, errMsg, eur, serviceAmount, servicePriceLabel, todayStr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LoginGate } from "./LoginGate";

function Step({ n, title, children }) {
  return (
    <section className="space-y-3">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cyan-700">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-cyan-100 text-[10px]">{n}</span> {title}
      </p>
      {children}
    </section>
  );
}

export function SlotPicker({ proId, date, value, onChange }) {
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    if (!proId || !date) return;
    api.get(`/professionals/${proId}/slots`, { params: { date } }).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, [proId, date]);
  if (!slots.length) return <p className="text-sm text-slate-500" data-testid="no-slots">Nessuna disponibilità in questa data (lun–ven).</p>;
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
      {slots.map((s) => (
        <button
          key={s.time} disabled={!s.available} data-testid={`slot-${s.time}`} onClick={() => onChange(s.time)}
          className={`rounded-lg border px-2 py-1.5 font-mono text-xs transition-colors ${value === s.time ? "border-sky-600 bg-sky-600 text-white" : s.available ? "border-slate-200 bg-white hover:border-cyan-400" : "cursor-not-allowed border-slate-100 bg-slate-100 text-slate-300 line-through"}`}
        >
          {s.time}
        </button>
      ))}
    </div>
  );
}

export function BookingFlow({ kind }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [pros, setPros] = useState([]);
  const [q, setQ] = useState("");
  const [pro, setPro] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/services", { params: { kind } }).then(({ data }) => setServices(data));
  }, [kind]);
  useEffect(() => {
    if (!service) return;
    api.get("/professionals", { params: { kind, specialty: service.specialty, q: q || undefined } }).then(({ data }) => setPros(data));
  }, [service, q, kind]);
  const canBook = useMemo(() => service && pro && date && time, [service, pro, date, time]);

  const book = async () => {
    setBusy(true);
    try {
      const { data: appt } = await api.post("/appointments", { service_id: service.id, professional_id: pro.id, date, time, notes });
      if (appt.status === "in_attesa_pagamento") {
        const { data: pay } = await api.post(`/payments/checkout/${appt.id}`);
        navigate(`/pagamento/${pay.id}`);
      } else {
        toast.success("Prenotazione confermata");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={`booking-flow-${kind}`}>
      <Step n="1" title={kind === "video" ? "Specializzazione" : "Prestazione"}>
        <div className="grid gap-2">
          {services.map((s) => (
            <button key={s.id} data-testid={`service-option-${s.id}`} onClick={() => { setService(s); setPro(null); setTime(null); }}
              className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors ${service?.id === s.id ? "border-sky-600 bg-sky-50" : "border-slate-200 bg-white hover:border-cyan-400"}`}>
              <span><b className="block text-slate-800">{s.specialty}</b><span className="text-xs text-slate-500">{s.name} · {s.duration_min} min</span></span>
              <span className="font-mono text-sm font-semibold text-sky-700">{servicePriceLabel(s)}</span>
            </button>
          ))}
        </div>
      </Step>
      {service && (
        <Step n="2" title="Professionista">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="professional-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome…" className="pl-9" />
          </div>
          <div className="grid gap-2">
            {pros.length === 0 && <p className="text-sm text-slate-500">Nessun professionista disponibile.</p>}
            {pros.map((p) => (
              <button key={p.id} data-testid={`professional-option-${p.id}`} onClick={() => { setPro(p); setTime(null); }}
                className={`rounded-xl border px-4 py-3 text-left text-sm transition-colors ${pro?.id === p.id ? "border-sky-600 bg-sky-50" : "border-slate-200 bg-white hover:border-cyan-400"}`}>
                <b className="text-slate-800">{p.title} {p.name}</b>
                <span className="block text-xs text-slate-500">{p.specialty} · {p.bio}</span>
              </button>
            ))}
          </div>
        </Step>
      )}
      {pro && (
        <Step n="3" title="Data e orario disponibili">
          <Input type="date" data-testid="booking-date-input" min={todayStr()} value={date} onChange={(e) => { setDate(e.target.value); setTime(null); }} />
          <SlotPicker proId={pro.id} date={date} value={time} onChange={setTime} />
        </Step>
      )}
      {time && (
        <Step n="4" title="Conferma">
          <Textarea data-testid="booking-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note amministrative (non inserire dati clinici sensibili)" />
          {user ? (
            <Button data-testid="booking-confirm-btn" disabled={!canBook || busy} onClick={book} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">
              <CheckCircle2 className="mr-2 h-4 w-4" /> Prenota e procedi al pagamento · {eur(serviceAmount(service))}
            </Button>
          ) : <LoginGate />}
        </Step>
      )}
    </div>
  );
}
